import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import MuiSlider from '@material-ui/core/Slider';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { capitalize } from '@material-ui/core';
import { fade } from '@material-ui/core/styles/colorManipulator';

const MUI_COLORS = [
  'primary',
  'secondary',
];

export const COLORS = [
  ...MUI_COLORS,
  'accentGreen',
  'accentBlue',
  'accentBlue2',
  'darkGray',
  'lightGray',
  'primaryGray',
  'red',
  'orange',
  'seafoam',
];

const getColorProp = (color) => {
  if (MUI_COLORS.includes(color)) {
    return color;
  }

  return 'primary'; // Must be this or secondary to satisfy prop types
};

const getRootColor = (color) => {
  return {
    color,
  };
};

const getThumbColor = (color) => {
  return {
    '&$focusVisible,&:hover': {
      boxShadow: `0px 0px 0px 8px ${fade(color, 0.16)}`,
    },
    '&$active': {
      boxShadow: `0px 0px 0px 14px ${fade(color, 0.16)}`,
    },
  };
};

const useCustomStyles = makeStyles(theme => ({
  rootColorAccentBlue: getRootColor(theme.palette.facet.accentBlue),
  rootColorAccentBlue2: getRootColor(theme.palette.facet.accentBlue2),
  rootColorDarkGray: getRootColor(theme.palette.facet.darkGray),
  rootColorLightGray: getRootColor(theme.palette.facet.lightGray),
  rootColorPrimaryGray: getRootColor(theme.palette.facet.primaryGray),
  rootColorRed: getRootColor(theme.palette.facet.red),
  rootColorOrange: getRootColor(theme.palette.facet.orange),
  rootColorSeafoam: getRootColor(theme.palette.facet.seafoam),
  rootColorAccentGreen: getRootColor(theme.palette.facet.accentGreen),
  thumbColorAccentBlue: getThumbColor(theme.palette.facet.accentBlue),
  thumbColorAccentBlue2: getThumbColor(theme.palette.facet.accentBlue2),
  thumbColorDarkGray: getThumbColor(theme.palette.facet.darkGray),
  thumbColorLightGray: getThumbColor(theme.palette.facet.lightGray),
  thumbColorPrimaryGray: getThumbColor(theme.palette.facet.primaryGray),
  thumbColorRed: getThumbColor(theme.palette.facet.red),
  thumbColorOrange: getThumbColor(theme.palette.facet.orange),
  thumbColorSeafoam: getThumbColor(theme.palette.facet.seafoam),
  thumbColorAccentGreen: getThumbColor(theme.palette.facet.accentGreen),
}));

const styles = () => ({
  root: {},
  thumb: {},
});

function Slider({
  className,
  classes,
  color,
  onChange,
  ...rest
}) {
  const {
    root: rootClass,
    thumb: thumbClass,
    ...restClasses
  } = classes;

  const customClasses = useCustomStyles();

  return (
    <MuiSlider
      {...rest}
      classes={{
        ...restClasses,
        root: classNames(rootClass, className, customClasses[`rootColor${capitalize(color)}`]),
        thumb: classNames(thumbClass, customClasses[`thumbColor${capitalize(color)}`]),
      }}
      color={getColorProp(color)}
      onChange={(evt, value) => {
        return onChange(String(value));
      }}
    />
  );
}

Slider.defaultProps = {
  className: '',
  color: 'primary',
};

Slider.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  color: PropTypes.oneOf(COLORS),
  onChange: PropTypes.func.isRequired,
};

export default withStyles(styles)(Slider);
